import useRoutesHandler from "@/Hooks/useRouteHandler";
import useToggleHandler from "@/Hooks/useToggleHandler";

import URLS from "@repo/api-services/URLS";
import { handleNetworkError } from "@/utility/networkError.utility";
import { useGetAllGeoScopeService } from "@repo/api-services/admin";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { handleSuccessToast } from "@/UI/Toasts";
import { TBasicObject } from "@repo/api-services/API-TYPES";
import { IGeoScopeResponseSchema } from "../geoscope.type";
import {
  handleGlobalDeleteRequest,
  handleGlobalPostRequest,
  handleGlobalPutRequest,
} from "@repo/api-services/changeRequestsGlobal.ts";

function useGeoScopes({ enablePagination = false, enablePerPage = false }) {
  // STATES
  const [editData, setEditData] = useState<IGeoScopeResponseSchema | null>(
    null
  );
  const [deleteRow, setDeleteRow] = useState<string | undefined>();

  // HOOKS
  const { isOpen, toggler } = useToggleHandler();
  const { isOpen: isConfirmModalOpen, toggler: toggleConfirm } =
    useToggleHandler();
  const { urlQueries } = useRoutesHandler();

  // URL functions

  const page = enablePagination ? urlQueries.page || "1" : "1";
  const per_page = enablePerPage ? urlQueries.pagesize || "15" : "15";

  // SERVICES
  const {
    states: { searchQuery, setSearchQuery },
    service: { allGeoScopeService },
  } = useGetAllGeoScopeService({ page, per_page });

  // MUTATIONS
  const createGeoScopesMutation = useMutation({
    mutationFn: (data: TBasicObject) => {
      return handleGlobalPostRequest({ url: URLS.GEO_SCOPES, data });
    },
    onSuccess: () => {
      allGeoScopeService.refetch();
      handleSuccessToast({ message: "Geo Scope Created Successfully" });
      toggleAddEditModal();
    },
    onError: ({ response }: { response: Error }) => {
      handleNetworkError(response);
    },
  });

  const updateGeoScopeMutation = useMutation({
    mutationFn: (data: TBasicObject) =>
      handleGlobalPutRequest({
        url: `${URLS.GEO_SCOPES}/${data.id}`,
        data,
      }),
    onSuccess: () => {
      allGeoScopeService.refetch();
      handleSuccessToast({ message: "Geo Scope Updated Successfully" });
      toggleAddEditModal();
    },
    onError: ({ response }: { response: Error }) => {
      handleNetworkError(response);
    },
  });

  const deleteGeoScopeMutation = useMutation({
    mutationFn: (id: string) =>
      handleGlobalDeleteRequest({
        url: `${URLS.GEO_SCOPES}/${id}`,
      }),
    onSuccess: () => {
      allGeoScopeService.refetch();
      handleSuccessToast({ message: "Geo Scope Deleted Successfully" });
    },
    onError: ({ response }: { response: Error }) => {
      handleNetworkError(response);
    },
  });

  // Functions
  function handleAddEditGeoScope(data: any) {
    if (editData?.id) {
      updateGeoScopeMutation.mutate({
        ...data,
        id: editData?.id,
      });
      return;
    }
    createGeoScopesMutation.mutate({
      ...data,
    });
  }

  function toggleAddEditModal() {
    if (isOpen) {
      setEditData(null);
    }
    toggler();
  }

  function handleDeleteGeoScope(id: string) {
    deleteGeoScopeMutation.mutate(id);
    toggleConfirm();
  }
  return {
    service: { allGeoScopeService },
    confirmModal: { isOpen: isConfirmModalOpen, toggler: toggleConfirm },
    mutation: {
      createGeoScopesMutation,
      updateGeoScopeMutation,
      deleteGeoScopeMutation,
    },
    states: {
      editData,
      isOpen,
      deleteRow,
      searchQuery,
      setSearchQuery,
      setDeleteRow,
      setEditData,
    },

    functions: {
      handleAddEditGeoScope,
      handleDeleteGeoScope,
      toggleAddEditModal,
    },
  };
}
export default useGeoScopes;
